import { useEffect, useRef, useState } from 'react';
import {
  useCustom,
  useCustomMutation,
  useNotification,
  useTranslate,
  type HttpError,
} from '@refinedev/core';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Empty,
  Flex,
  Popconfirm,
  Skeleton,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import type {
  NovelistDeliveryConfigurationOut,
  NovelistDeliveryConfigurationUpdateIn,
} from '@/api/generated/schemas';
import { NovelistDeliveryStatus } from '@/api/generated/schemas';
import { orDash } from '@/components/common/show/shared';
import { extractBackendMessage } from '@/utils/apiError';
import { formatDateTime } from '@/utils/format';
import { DeliveryConfigurationModal } from './DeliveryConfigurationModal';

const isNotFound = (error: HttpError | null) => error?.statusCode === 404;

interface PublisherIntegrationTabProps {
  publisherId: number;
  /** When the publisher was last fetched; a refetch reloads the configuration. */
  publisherFetchedAt: number;
}

export const PublisherIntegrationTab = ({
  publisherId,
  publisherFetchedAt,
}: PublisherIntegrationTabProps) => {
  const t = useTranslate();
  const { open: notify } = useNotification();
  const { mutateAsync } = useCustomMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const tk = (suffix: string) =>
    t(`content_catalog.publishers.integration.${suffix}`);
  const tf = (field: string) => tk(`fields.${field}`);

  const url = `content_catalog/publishers/${publisherId}/delivery_configuration`;

  const { query } = useCustom<NovelistDeliveryConfigurationOut>({
    url,
    method: 'get',
    // A publisher without a configuration answers 404 — the "not configured" state.
    errorNotification: false,
    queryOptions: {
      retry: (count, error) => !isNotFound(error) && count < 3,
    },
  });
  const configuration = query.data?.data;

  const lastPublisherFetch = useRef(publisherFetchedAt);
  useEffect(() => {
    if (publisherFetchedAt !== lastPublisherFetch.current) {
      lastPublisherFetch.current = publisherFetchedAt;
      query.refetch();
    }
  }, [publisherFetchedAt, query]);

  const switchStatus = async (status: NovelistDeliveryStatus) => {
    const payload: NovelistDeliveryConfigurationUpdateIn = { status };
    setSwitching(true);
    try {
      await mutateAsync({
        url,
        method: 'patch',
        values: payload,
        successNotification: false,
        errorNotification: false,
      });
      notify?.({
        type: 'success',
        message: t('notifications.success'),
        description: tk(`status_change.${status}.success`),
      });
      query.refetch();
    } catch (error) {
      notify?.({
        type: 'error',
        message: t('notifications.error'),
        description:
          extractBackendMessage(error) ?? tk('status_change.error_fallback'),
      });
    } finally {
      setSwitching(false);
    }
  };

  const modal = (
    <DeliveryConfigurationModal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      publisherId={publisherId}
      configuration={configuration}
      onSaved={() => query.refetch()}
    />
  );

  const title = tk('title');

  if (query.isLoading) {
    return (
      <Card title={title}>
        <Skeleton active />
      </Card>
    );
  }

  if (query.isError && !isNotFound(query.error)) {
    return (
      <Card title={title}>
        <Alert
          type="error"
          showIcon
          message={tk('load_error')}
          description={extractBackendMessage(query.error)}
          action={
            <Button size="small" onClick={() => query.refetch()}>
              {t('buttons.refresh')}
            </Button>
          }
        />
      </Card>
    );
  }

  if (!configuration) {
    return (
      <Card title={title}>
        <Empty description={tk('empty.description')}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setModalOpen(true)}
          >
            {tk('empty.button')}
          </Button>
        </Empty>
        {modal}
      </Card>
    );
  }

  const isActive = configuration.status === NovelistDeliveryStatus.active;

  return (
    <Card
      title={title}
      extra={
        isActive ? (
          <Popconfirm
            title={tk('status_change.inactive.confirm_title')}
            description={tk('status_change.inactive.confirm_description')}
            okText={tk('status_change.inactive.button')}
            okButtonProps={{ danger: true }}
            cancelText={t('buttons.cancel')}
            onConfirm={() => switchStatus(NovelistDeliveryStatus.inactive)}
          >
            <Button danger icon={<PauseCircleOutlined />} loading={switching}>
              {tk('status_change.inactive.button')}
            </Button>
          </Popconfirm>
        ) : (
          <Button
            icon={<PlayCircleOutlined />}
            loading={switching}
            onClick={() => switchStatus(NovelistDeliveryStatus.active)}
          >
            {tk('status_change.active.button')}
          </Button>
        )
      }
    >
      <Descriptions bordered size="small" column={1}>
        <Descriptions.Item label={tf('method')}>
          <Tag>{tk(`method.${configuration.method}`)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={tf('status')}>
          <Tag color={isActive ? 'green' : 'default'}>
            {tk(`status.${configuration.status}`)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={tf('delivery_account')}>
          <Flex align="center" gap="small">
            {configuration.delivery_account ? (
              <Typography.Text copyable>
                {configuration.delivery_account}
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary">
                {tk('account_missing')}
              </Typography.Text>
            )}
            <Button
              size="small"
              type="text"
              icon={<EditOutlined />}
              aria-label={tk('account.title')}
              onClick={() => setModalOpen(true)}
            />
          </Flex>
        </Descriptions.Item>
        <Descriptions.Item label={tf('home_directory')}>
          <Flex vertical gap={4}>
            <Typography.Text code copyable>
              {configuration.home_directory}
            </Typography.Text>
            <Typography.Text type="secondary">
              {tk('hints.home_directory')}
            </Typography.Text>
          </Flex>
        </Descriptions.Item>
        <Descriptions.Item label={tf('storage_prefix')}>
          <Typography.Text code>{configuration.storage_prefix}</Typography.Text>
        </Descriptions.Item>
        <Descriptions.Item label={t('base.fields.created_at')}>
          {orDash(formatDateTime(configuration.created_at))}
        </Descriptions.Item>
        <Descriptions.Item label={t('base.fields.updated_at')}>
          {orDash(formatDateTime(configuration.updated_at))}
        </Descriptions.Item>
      </Descriptions>
      {modal}
    </Card>
  );
};
